import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabase';
import AppLayout from '../../components/Layout/ParentAccountLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import Input from '../../components/UI/Input';

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: resetError } =
        await supabase.auth.resetPasswordForEmail(
          normalizedEmail,
          {
            redirectTo: `${window.location.origin}/reset-password`,
          }
        );

      if (resetError) {
        throw resetError;
      }

      setEmailSent(true);
    } catch (submitError) {
      console.error(
        'Forgot password request error:',
        submitError
      );

      const message = String(
        submitError?.message || ''
      ).toLowerCase();

      if (
        message.includes('rate limit') ||
        message.includes('too many')
      ) {
        setError(
          'Please wait a little before requesting another password reset email.'
        );
      } else {
        setError(
          'Unable to send password reset instructions right now. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AppLayout title="MyPiggy">
        <div className="max-w-md mx-auto">
          <Card className="p-4 sm:p-5">
            <div className="text-center">
              <MyPiggyIcon name="money-received" size="lg" className="mb-3" />

              <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
                Check your email
              </h2>

              <p className="text-gray-600 dark:text-slate-300 leading-relaxed mb-4">
                If a MyPiggy parent account exists for that email,
                we sent password reset instructions.
              </p>

              <div className="bg-blue-50 dark:bg-blue-950/35 border border-blue-100 rounded-xl p-5 mb-4">
                <p className="text-sm text-gray-600 dark:text-slate-300 mb-2">
                  Password reset requested for:
                </p>

                <p className="font-semibold text-gray-900 dark:text-slate-100 break-words">
                  {email.trim().toLowerCase()}
                </p>
              </div>

              <p className="text-sm text-gray-500 dark:text-slate-300 leading-relaxed mb-4">
                Open the email and click the password reset link.
                If you do not see it, check your spam or junk folder.
              </p>

              <div className="space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => navigate('/parent/login')}
                >
                  Back to Parent Login
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setEmailSent(false);
                    setError('');
                  }}
                  className="w-full text-blue-600 dark:text-blue-400 hover:text-blue-500 font-medium text-sm"
                >
                  Try another email
                </button>
              </div>
            </div>
          </Card>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500 dark:text-slate-300">
              Real piggy bank. Digital tracking.
              <br />
              Lifelong money lessons.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="MyPiggy">
      <div className="max-w-md mx-auto">
        <Card className="p-4 sm:p-5">
          <div className="text-center mb-4">
            <MyPiggyIcon name="parent-approval" size="lg" className="mb-3" />

            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-2">
              Forgot your password?
            </h2>

            <p className="text-gray-600 dark:text-slate-300">
              Enter your parent account email and we will send
              you a password reset link.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <Input
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);

                if (error) {
                  setError('');
                }
              }}
              required
              autoFocus
            />

            {error && (
              <div
                className="text-red-700 dark:text-red-300 text-sm bg-red-50 dark:bg-red-950/35 border border-red-100 p-3 rounded-lg"
                role="alert"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading
                ? 'Sending Reset Email...'
                : 'Send Password Reset Email'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/parent/login')}
              disabled={loading}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back to Parent Login
            </button>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ForgotPassword;

